/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  RefreshCw,
  AlertTriangle,
  ServerCrash,
  Lock,
  Clock,
  Coins,
} from "lucide-react";

/**
 * VEST-3 — Read-only admin vesting dashboard.
 *
 * Strictly read-only: no claim, transfer, approval, signature, or
 * wallet connect. The single network call is
 * `GET /api/admin/vesting/summary[?refresh=1]`, gated by the same
 * `arc_admin_token` bearer pattern used by `AdminTournaments`.
 *
 * Shadow mode (`status="not_deployed"`) renders honest zeros and a
 * cyan "Contract not deployed yet" badge. RPC failure renders a
 * yellow warning, never seeded values.
 */
const TOKEN_KEY = "arc_admin_token";

const RESPONSE_KEYS = [
  "status", "contract_live", "contract_address", "token_address",
  "beneficiary", "total_allocation", "vested_amount", "releasable_amount",
  "released_amount", "unvested_amount", "start_at", "cliff_end_at", "end_at",
  "cliff_seconds", "duration_seconds", "refreshed_at", "cache_hit", "error",
];

const ARC_DECIMALS = 18;

async function authedFetch(path, token, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${text || res.statusText}`);
  }
  return res.json();
}

function formatArc(weiStr) {
  if (weiStr === undefined || weiStr === null) return "—";
  try {
    const n = BigInt(weiStr);
    const base = 10n ** BigInt(ARC_DECIMALS);
    const whole = n / base;
    const frac = n - whole * base;
    if (frac === 0n) return whole.toLocaleString();
    // Two decimal digits for the dashboard tiles.
    const twoDigits = (frac * 100n) / base;
    return `${whole.toLocaleString()}.${twoDigits.toString().padStart(2, "0")}`;
  } catch {
    return weiStr;
  }
}

function shortAddr(a) {
  if (!a) return "—";
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

function humanDuration(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const days = Math.floor(seconds / 86400);
  const months = (seconds / (30 * 86400)).toFixed(1);
  return `${seconds.toLocaleString()} s · ${days.toLocaleString()} d · ~${months} months`;
}

export default function AdminVesting() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || "",
  );
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSummary = useCallback(async (tk, opts = {}) => {
    const force = !!opts.refresh;
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const path = force
        ? `/admin/vesting/summary?refresh=1`
        : `/admin/vesting/summary`;
      const out = await authedFetch(path, tk);
      setSummary(out);
    } catch (e) {
      const msg = String(e.message || "");
      const is401or403 = msg.startsWith("401") || msg.startsWith("403");
      if (is401or403) {
        toast.error(`auth rejected: ${msg}`);
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        toast.error(`load failed: ${msg}`);
      }
    } finally {
      if (force) setRefreshing(false); else setLoading(false);
    }
  }, []);

  // StrictMode in dev fires effects twice; guard concurrent fetches so
  // we don't end up sharing a single Response between two readers.
  const inFlightRef = React.useRef(false);

  useEffect(() => {
    if (!authed || !token) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    fetchSummary(token).finally(() => {
      inFlightRef.current = false;
    });
  }, [authed, token, fetchSummary]);

  const onLogin = async () => {
    if (!token) {
      toast.error("token required");
      return;
    }
    try {
      await authedFetch(`/admin/vesting/summary`, token);
    } catch (e) {
      toast.error(`auth failed: ${e.message}`);
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
    setAuthed(true);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setSummary(null);
  };

  // -------- Render branches --------

  if (!authed) {
    return (
      <LoginShell
        token={token}
        setToken={setToken}
        onLogin={onLogin}
      />
    );
  }

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      data-testid="admin-vesting-page"
    >
      <header className="border-b border-white/5 bg-black/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Lock className="h-4 w-4 text-[#00F0FF]" />
          <div className="flex-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
              ArenaCoin · Operator
            </p>
            <h1 className="font-display text-xl font-black">Vesting Dashboard</h1>
          </div>
          <button
            type="button"
            onClick={() => fetchSummary(token, { refresh: true })}
            disabled={loading || refreshing}
            data-testid="admin-vesting-refresh-btn"
            className="inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-vesting-logout-btn"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-[#FF3B30] transition-colors"
          >
            Log out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <HardRuleBanner />

        {loading && !summary && (
          <div
            data-testid="admin-vesting-loading"
            className="font-mono text-xs text-white/50 py-12 text-center"
          >
            loading vesting summary…
          </div>
        )}

        {summary && <StatusCard summary={summary} />}
        {summary && summary.status === "live" && <TotalsGrid summary={summary} />}
        {summary && summary.status === "not_deployed" && (
          <NotDeployedTotals />
        )}
        {summary && summary.status === "rpc_unavailable" && (
          <RpcWarning summary={summary} />
        )}
        {summary && <BeneficiaryRow summary={summary} />}
        {summary && <ScheduleSection summary={summary} />}
        {summary && <MetaFooter summary={summary} />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoginShell({ token, setToken, onLogin }) {
  return (
    <div
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4"
      data-testid="admin-vesting-login"
    >
      <div className="w-full max-w-md glass rounded-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-4 w-4 text-[#00F0FF]" />
          <h1 className="font-display text-lg font-black">Operator sign-in</h1>
        </div>
        <p className="font-mono text-[11px] text-white/60 mb-4">
          Vesting Dashboard is operator-only. Provide your{" "}
          <code className="text-[#00F0FF]">ARC_ADMIN_TOKEN</code> bearer token to
          continue. The token is stored in browser localStorage.
        </p>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onLogin()}
          placeholder="ARC_ADMIN_TOKEN"
          data-testid="admin-vesting-token-input"
          className="w-full rounded border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#00F0FF]/60"
        />
        <button
          type="button"
          onClick={onLogin}
          data-testid="admin-vesting-login-btn"
          className="mt-3 w-full rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}

function HardRuleBanner() {
  return (
    <div
      data-testid="admin-vesting-hardrule-banner"
      className="rounded-md border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4 flex items-start gap-3"
    >
      <ShieldCheck className="h-4 w-4 mt-0.5 text-[#00F0FF] shrink-0" />
      <p className="font-mono text-[11px] leading-relaxed text-white/80">
        Read-only dashboard. No claim, transfer, approval, or wallet
        action exists here. The on-chain <code>release()</code> is
        permissionless and is{" "}
        <strong className="text-[#00F0FF]">not</strong> invoked from this
        page. No signature, no transaction, no token transfer is
        initiated by the operator console.
      </p>
    </div>
  );
}

function StatusCard({ summary }) {
  const status = summary.status;
  const tone =
    status === "live"
      ? "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]"
      : status === "not_deployed"
        ? "border-[#00F0FF]/40 bg-[#00F0FF]/5 text-[#00F0FF]"
        : "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]";
  const label =
    status === "live"
      ? "LIVE · contract deployed"
      : status === "not_deployed"
        ? "PRE-LAUNCH · contract not deployed yet"
        : "RPC UNAVAILABLE · cached values may be stale";
  return (
    <div
      data-testid="admin-vesting-status-card"
      data-vesting-status={status}
      className="glass rounded-md p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
          Deployment status
        </span>
        <span
          data-testid="admin-vesting-status-badge"
          className={`rounded border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${tone}`}
        >
          {label}
        </span>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] font-mono">
        <div>
          <dt className="text-white/40 uppercase tracking-[0.18em] text-[9px]">Contract</dt>
          <dd
            data-testid="admin-vesting-contract-address"
            className="text-white/80 break-all"
            title={summary.contract_address || ""}
          >
            {summary.contract_address || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-white/40 uppercase tracking-[0.18em] text-[9px]">ARC token</dt>
          <dd
            data-testid="admin-vesting-token-address"
            className="text-white/80 break-all"
            title={summary.token_address || ""}
          >
            {summary.token_address || "—"}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function TotalsGrid({ summary }) {
  const tiles = [
    { key: "total_allocation",  label: "Total allocated",  tone: "text-white" },
    { key: "vested_amount",     label: "Vested",           tone: "text-[#39FF14]" },
    { key: "releasable_amount", label: "Claimable now",    tone: "text-[#00F0FF]" },
    { key: "released_amount",   label: "Claimed",          tone: "text-white/70" },
    { key: "unvested_amount",   label: "Unvested",         tone: "text-[#FFB800]" },
  ];
  return (
    <div
      data-testid="admin-vesting-totals-grid"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
    >
      {tiles.map(({ key, label, tone }) => (
        <div
          key={key}
          data-testid={`admin-vesting-tile-${key}`}
          className="glass rounded-md p-4"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40 mb-2">
            {label}
          </p>
          <p className={`font-display text-2xl font-black ${tone}`}>
            {formatArc(summary[key])}
          </p>
          <p className="mt-1 font-mono text-[9px] text-white/30">ARC</p>
        </div>
      ))}
    </div>
  );
}

function NotDeployedTotals() {
  // Honest zeros — never seeded. Same five tiles as the live grid,
  // dimmed.
  const tiles = [
    "total_allocation", "vested_amount", "releasable_amount",
    "released_amount", "unvested_amount",
  ];
  return (
    <div
      data-testid="admin-vesting-not-deployed-grid"
      data-balance-state="pre-launch"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
    >
      {tiles.map((key) => (
        <div
          key={key}
          data-testid={`admin-vesting-tile-${key}`}
          className="glass rounded-md p-4 opacity-60"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40 mb-2">
            {key.replace(/_/g, " ")}
          </p>
          <p className="font-display text-2xl font-black text-white/40">0</p>
          <p className="mt-1 font-mono text-[9px] text-white/30">ARC</p>
        </div>
      ))}
    </div>
  );
}

function RpcWarning({ summary }) {
  return (
    <div
      data-testid="admin-vesting-rpc-warning"
      data-vesting-error={summary.error || "unknown"}
      className="rounded-md border border-[#FFB800]/40 bg-[#FFB800]/5 p-4 flex items-start gap-3"
    >
      <ServerCrash className="h-4 w-4 mt-0.5 text-[#FFB800] shrink-0" />
      <div className="flex-1 font-mono text-[11px] text-[#FFB800]">
        On-chain RPC is unavailable. Totals are <strong>not seeded</strong>;
        all numeric tiles render zero until a fresh read succeeds. Error
        code:{" "}
        <code data-testid="admin-vesting-rpc-error-code">
          {summary.error || "unknown"}
        </code>
      </div>
    </div>
  );
}

function BeneficiaryRow({ summary }) {
  return (
    <div
      data-testid="admin-vesting-beneficiary-card"
      className="glass rounded-md p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
          Beneficiary
        </span>
        <Coins className="h-3.5 w-3.5 text-white/30" />
      </div>
      <p
        data-testid="admin-vesting-beneficiary-address"
        className="font-mono text-sm text-white/85"
        title={summary.beneficiary || ""}
      >
        {summary.beneficiary || "—"}
      </p>
      <p className="mt-1 font-mono text-[10px] text-white/40">
        {summary.beneficiary
          ? `Single immutable beneficiary (${shortAddr(summary.beneficiary)})`
          : "Beneficiary is read from the on-chain immutable once the contract is deployed."}
      </p>
    </div>
  );
}

function ScheduleSection({ summary }) {
  return (
    <div
      data-testid="admin-vesting-schedule-card"
      className="glass rounded-md p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-3.5 w-3.5 text-[#00F0FF]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
          Schedule
        </span>
      </div>

      <ScheduleCurve summary={summary} />

      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[11px] font-mono mt-4">
        <ScheduleRow
          label="Start"
          value={formatTs(summary.start_at)}
          testid="admin-vesting-start-at"
        />
        <ScheduleRow
          label="Cliff end"
          value={formatTs(summary.cliff_end_at)}
          testid="admin-vesting-cliff-end-at"
        />
        <ScheduleRow
          label="End"
          value={formatTs(summary.end_at)}
          testid="admin-vesting-end-at"
        />
        <ScheduleRow
          label="Cliff"
          value={humanDuration(summary.cliff_seconds)}
          testid="admin-vesting-cliff-seconds"
        />
        <ScheduleRow
          label="Duration after cliff"
          value={humanDuration(summary.duration_seconds)}
          testid="admin-vesting-duration-seconds"
          full
        />
      </dl>
    </div>
  );
}

/**
 * VEST-3.1 — Pure SVG read-only schedule curve.
 *
 * Renders a flat-then-linear vesting ramp:
 *   - x-axis: start → cliff_end → end
 *   - y-axis: 0% → 100% vested
 *   - flat at 0% until cliff_end
 *   - linear ramp from cliff_end to end
 *   - "now" cursor when start_at + end_at are valid and `now`
 *     is within the [start, end] window
 *
 * Shadow mode (`status === "not_deployed"`) renders a dimmed curve
 * computed from the cliff_seconds + duration_seconds env-config
 * defaults (NOT live unlock values) and overlays a clear
 * "Contract not deployed yet" caption. No fake percentages.
 *
 * RPC-unavailable renders a neutral placeholder.
 */
function ScheduleCurve({ summary }) {
  const W = 600;
  const H = 175;
  const XL = 40, XR = 580;
  const YB = 130, YT = 20;
  const status = summary?.status;

  const startMs = summary?.start_at ? Date.parse(summary.start_at) : NaN;
  const cliffMs = summary?.cliff_end_at ? Date.parse(summary.cliff_end_at) : NaN;
  const endMs   = summary?.end_at ? Date.parse(summary.end_at) : NaN;

  const hasLive = (
    status === "live"
    && Number.isFinite(startMs)
    && Number.isFinite(cliffMs)
    && Number.isFinite(endMs)
    && endMs > startMs
    && cliffMs >= startMs
    && cliffMs <= endMs
  );

  // For shadow/unavailable, build a synthetic curve from the
  // schedule constants so the shape is visible. Fall back to a flat
  // 0–25–100% split if even those are missing.
  let xCliff, xEnd, xNow = null, isNowOnFlat = false;
  if (hasLive) {
    const totalMs = endMs - startMs;
    xCliff = XL + ((cliffMs - startMs) / totalMs) * (XR - XL);
    xEnd = XR;
    const now = Date.now();
    if (now >= startMs && now <= endMs) {
      xNow = XL + ((now - startMs) / totalMs) * (XR - XL);
      isNowOnFlat = now <= cliffMs;
    } else if (now > endMs) {
      xNow = XR;
    } else {
      xNow = XL;
    }
  } else {
    // Synthetic curve from constants when available.
    const cs = summary?.cliff_seconds ?? 15_552_000; // 180d default
    const ds = summary?.duration_seconds ?? 62_208_000; // 720d default
    const total = cs + ds;
    xCliff = XL + (cs / total) * (XR - XL);
    xEnd = XR;
  }

  const path = `M ${XL} ${YB} L ${xCliff} ${YB} L ${xEnd} ${YT}`;

  const greyed = !hasLive;
  const strokeColor = greyed ? "#666" : "#39FF14";
  const flatStroke = greyed ? "#444" : "#FFB800";

  // ---- x-axis date ticks (4 ticks: start / cliff / mid / end) ----
  // Live: render compact "Mon YYYY" from real timestamps.
  // Shadow + RPC-unavailable: derive synthetic day-offset labels from
  // cliff_seconds + duration_seconds. No tooltips, no extra state.
  const monLabel = (ms) => {
    if (!Number.isFinite(ms)) return "—";
    try {
      return new Date(ms).toLocaleString(undefined, {
        month: "short", year: "numeric",
      });
    } catch {
      return "—";
    }
  };
  const cs = summary?.cliff_seconds ?? 15_552_000;
  const ds = summary?.duration_seconds ?? 62_208_000;
  const midX = xCliff + (xEnd - xCliff) / 2;
  const axisTicks = hasLive
    ? [
        { key: "start", testid: "vesting-axis-tick-start",
          x: XL, label: monLabel(startMs), anchor: "start" },
        { key: "cliff", testid: "vesting-axis-tick-cliff",
          x: xCliff, label: monLabel(cliffMs), anchor: "middle" },
        { key: "mid",   testid: "vesting-axis-tick-mid",
          x: midX,
          label: monLabel((cliffMs + endMs) / 2), anchor: "middle" },
        { key: "end",   testid: "vesting-axis-tick-end",
          x: xEnd, label: monLabel(endMs), anchor: "end" },
      ]
    : [
        { key: "start", testid: "vesting-axis-tick-start",
          x: XL, label: "Day 0", anchor: "start" },
        { key: "cliff", testid: "vesting-axis-tick-cliff",
          x: xCliff,
          label: `+${Math.round(cs / 86400)}d`,
          anchor: "middle" },
        { key: "mid",   testid: "vesting-axis-tick-mid",
          x: midX,
          label: `+${Math.round((cs + ds / 2) / 86400)}d`,
          anchor: "middle" },
        { key: "end",   testid: "vesting-axis-tick-end",
          x: xEnd,
          label: `+${Math.round((cs + ds) / 86400)}d`,
          anchor: "end" },
      ];

  return (
    <div
      data-testid="admin-vesting-schedule-curve"
      data-curve-mode={hasLive ? "live" : status === "rpc_unavailable" ? "rpc_unavailable" : "shadow"}
      className="relative w-full"
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Vesting schedule curve (read-only)"
      >
        {/* Frame */}
        <rect
          x={XL} y={YT} width={XR - XL} height={YB - YT}
          fill="none" stroke="rgba(255,255,255,0.06)"
        />
        {/* Y axis labels */}
        <text x={XL - 8} y={YT + 4} fill="#666" fontSize="9" textAnchor="end" fontFamily="monospace">100%</text>
        <text x={XL - 8} y={YB + 3} fill="#666" fontSize="9" textAnchor="end" fontFamily="monospace">0%</text>

        {/* Flat segment (0% pre-cliff) */}
        <line
          x1={XL} y1={YB} x2={xCliff} y2={YB}
          stroke={flatStroke}
          strokeWidth={greyed ? 1.5 : 2}
          strokeDasharray={greyed ? "4 4" : "0"}
        />
        {/* Ramp segment (cliff → end) */}
        <line
          x1={xCliff} y1={YB} x2={xEnd} y2={YT}
          stroke={strokeColor}
          strokeWidth={greyed ? 1.5 : 2}
          strokeDasharray={greyed ? "4 4" : "0"}
        />

        {/* Cliff marker */}
        <g data-testid="vesting-cliff-marker">
          <line
            x1={xCliff} y1={YT} x2={xCliff} y2={YB}
            stroke={greyed ? "#444" : "#FFB800"}
            strokeDasharray="3 3"
            strokeWidth={1}
            opacity={0.7}
          />
          <text
            x={xCliff} y={YT - 6}
            fill={greyed ? "#777" : "#FFB800"}
            fontSize="9" textAnchor="middle" fontFamily="monospace"
          >
            CLIFF
          </text>
        </g>

        {/* End marker */}
        <g data-testid="vesting-end-marker">
          <line
            x1={xEnd} y1={YT} x2={xEnd} y2={YB}
            stroke={greyed ? "#444" : "#39FF14"}
            strokeDasharray="3 3"
            strokeWidth={1}
            opacity={0.7}
          />
          <text
            x={xEnd} y={YT - 6}
            fill={greyed ? "#777" : "#39FF14"}
            fontSize="9" textAnchor="end" fontFamily="monospace"
          >
            END
          </text>
        </g>

        {/* Now cursor — only when live and inside the window */}
        {hasLive && xNow !== null && (
          <g
            data-testid="vesting-now-cursor"
            data-now-on-flat={isNowOnFlat ? "true" : "false"}
          >
            <line
              x1={xNow} y1={YT - 6} x2={xNow} y2={YB + 4}
              stroke="#00F0FF" strokeWidth={1.2}
            />
            <circle cx={xNow} cy={isNowOnFlat ? YB : YT + ((xNow - xCliff) / (xEnd - xCliff)) * (YB - YT) * 0 + YB - ((xNow - xCliff) / (xEnd - xCliff)) * (YB - YT)} r={3} fill="#00F0FF" />
            <text
              x={xNow} y={YB + 24}
              fill="#00F0FF" fontSize="9" textAnchor="middle" fontFamily="monospace"
            >
              NOW
            </text>
          </g>
        )}

        {/* X-axis date ticks — compact "Mon YYYY" labels (or day
            offsets when the contract is not deployed and no start
            timestamp exists). Pure SVG, no extra state. */}
        <g data-testid="vesting-axis-ticks">
          <line
            x1={XL} y1={YB} x2={xEnd} y2={YB}
            stroke="rgba(255,255,255,0.15)" strokeWidth={1}
          />
          {axisTicks.map((t) => (
            <g
              key={t.key}
              data-testid={t.testid}
              data-tick-mode={hasLive ? "live" : "synthetic"}
            >
              <line
                x1={t.x} y1={YB} x2={t.x} y2={YB + 4}
                stroke={greyed ? "#555" : "rgba(255,255,255,0.4)"}
                strokeWidth={1}
              />
              <text
                x={t.x}
                y={YB + 14}
                fill={greyed ? "#666" : "#aaa"}
                fontSize="8"
                textAnchor={t.anchor}
                fontFamily="monospace"
              >
                {t.label}
              </text>
            </g>
          ))}
        </g>
      </svg>

      {/* Shadow / unavailable overlay caption */}
      {!hasLive && (
        <div
          data-testid="vesting-curve-overlay-caption"
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <span
            className={`rounded border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${
              status === "rpc_unavailable"
                ? "border-[#FFB800]/40 bg-black/60 text-[#FFB800]"
                : "border-[#00F0FF]/40 bg-black/60 text-[#00F0FF]"
            }`}
          >
            {status === "rpc_unavailable"
              ? "RPC unavailable · schedule from config only"
              : "Pre-launch · contract not deployed yet"}
          </span>
        </div>
      )}
    </div>
  );
}

function ScheduleRow({ label, value, testid, full = false }) {
  return (
    <div className={full ? "sm:col-span-3" : ""}>
      <dt className="text-white/40 uppercase tracking-[0.18em] text-[9px]">
        {label}
      </dt>
      <dd data-testid={testid} className="text-white/80">
        {value}
      </dd>
    </div>
  );
}

function MetaFooter({ summary }) {
  return (
    <div
      data-testid="admin-vesting-meta-footer"
      className="rounded-md border border-white/5 bg-black/40 p-4 flex flex-wrap items-center gap-4 font-mono text-[10px] text-white/40"
    >
      <span>
        Last refreshed:{" "}
        <span data-testid="admin-vesting-refreshed-at" className="text-white/70">
          {formatTs(summary.refreshed_at)}
        </span>
      </span>
      <span
        data-testid="admin-vesting-cache-indicator"
        data-cache-hit={summary.cache_hit ? "true" : "false"}
      >
        Cache:{" "}
        <span
          className={
            summary.cache_hit ? "text-[#FFB800]" : "text-[#39FF14]"
          }
        >
          {summary.cache_hit ? "HIT (30 s window)" : "FRESH"}
        </span>
      </span>
      <span className="ml-auto inline-flex items-center gap-1 text-white/30">
        <AlertTriangle className="h-3 w-3" />
        no claim · no signature · no tx
      </span>
    </div>
  );
}
